/**
 * Artifacts panel — agent-generated deliverables (PPTX, PDF, DOCX, video, prototypes) for the dashboard right column.
 * Composition: artifacts/useArtifacts (list + actions), artifacts/ArtifactList, artifacts/GenerateArtifactModal.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Archive, Sparkles } from "lucide-react";
import { useArtifacts } from "./artifacts/useArtifacts";
import { ArtifactList } from "./artifacts/ArtifactList";
import { GenerateArtifactModal } from "./artifacts/GenerateArtifactModal";

const PanelHeader = ({ count, isExpanded, onToggle, onGenerate }) => (
  <div className="flex items-center justify-between p-4">
    <button onClick={onToggle} className="flex items-center gap-3 flex-1 hover:bg-white/5 transition-colors rounded-lg -m-2 p-2" data-testid="artifacts-toggle">
      <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center"><Archive className="h-4 w-4 text-orange-400" /></div>
      <div className="text-left">
        <h3 className="text-sm font-medium text-white">Artifacts</h3>
        <p className="text-xs text-gray-500">{count > 0 ? `${count} deliverables` : "PPTXs, videos, PRFAQs"}</p>
      </div>
    </button>
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={(e) => { e.stopPropagation(); onGenerate(); }}
        className="h-7 px-2 bg-ism-teal/20 hover:bg-ism-teal/30 text-ism-teal border border-ism-teal/30" data-testid="generate-artifact-btn">
        <Sparkles className="h-3 w-3 mr-1" />
        Generate
      </Button>
      {count > 0 && <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">{count}</Badge>}
      <button onClick={onToggle} className="p-1">
        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
    </div>
  </div>
);

const Artifacts = ({ userId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const data = useArtifacts(userId, isExpanded);
  const toggle = () => setIsExpanded((v) => !v);

  return (
    <>
      <div className="glass-card rounded-xl overflow-hidden" data-testid="artifacts-panel">
        <PanelHeader count={data.artifacts.length} isExpanded={isExpanded} onToggle={toggle} onGenerate={() => setShowGenerate(true)} />
        <AnimatePresence>
          {isExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-white/10">
              <div className="p-4 space-y-3 max-h-[350px] overflow-y-auto">
                <ArtifactList loading={data.loading} artifacts={data.artifacts} onView={data.viewArtifact} onDownload={data.downloadArtifact}
                  onDelete={data.deleteArtifact} onGenerate={() => setShowGenerate(true)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <GenerateArtifactModal open={showGenerate} onOpenChange={setShowGenerate} userId={userId} onGenerated={data.addArtifact} />
    </>
  );
};

export default Artifacts;
